/**
 * useState that survives navigation.
 *
 * View toggles live in component state, so leaving a page and coming back resets
 * them — someone who prefers browsing by album had to pick "Albums" every single
 * time. These are preferences, not transient UI state, so they belong in
 * localStorage.
 *
 * Stored values are validated against the allowed options on read. A key left
 * behind by an older build, or edited by hand, falls back to the default instead
 * of putting the UI into a state it cannot render.
 */

import { useCallback, useState } from 'react'

const PREFIX = 'tunes.'

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(PREFIX + key)
    return allowed.includes(stored as T) ? (stored as T) : fallback
  } catch {
    // Safari in private mode throws on localStorage access.
    return fallback
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIX + key, value)
  } catch {
    // A preference that cannot be saved is not worth interrupting anyone over.
  }
}

/**
 * @param key      storage key, namespaced under `tunes.` automatically
 * @param allowed  every legal value — anything else in storage is ignored
 * @param fallback used when nothing valid is stored
 */
export function usePersistentState<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => read(key, allowed, fallback))

  const update = useCallback(
    (next: T) => {
      setValue(next)
      write(key, next)
    },
    [key],
  )

  return [value, update]
}
